import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import { fetchDashboardData } from "@/store/slices/dashboardSlice";
import { setZoom } from "@/store/slices/designerSlice";
import { setCustomer, setId } from "@/store/slices/infoSlice";
import { RootState } from "@/store/store";
import { lazy, Suspense, useEffect } from "react";
import {
  // createBrowserRouter,
  Navigate,
  // RouterProvider,
   Routes, Route,
  BrowserRouter
} from "react-router-dom";
import { incrementCompleted, setTotal, } from "./store/slices/progressSlice";

const RootLayout = lazy(() => import("@/layouts/RootLayout"));
const DesignLayout = lazy(() => import("@/layouts/DesignLayout"));
const AddNotes = lazy(() => import("@/pages/AddNotes"));
const AddText = lazy(() => import("@/pages/AddText"));
const ApproveMockup = lazy(() => import("@/pages/ApproveMockup"));
const EditImage = lazy(() => import("@/pages/EditImage"));
const EditText = lazy(() => import("@/pages/EditText"));
const FontPreview = lazy(() => import("@/pages/FontPreview"));
const FontColor = lazy(() => import("@/pages/FontColor"));
const TextSize = lazy(() => import("@/pages/TextSize"));
const TextOutline = lazy(() => import("@/pages/TextOutline"));
const TextAlignment = lazy(() => import("@/pages/TextAlignment"));
const Cart = lazy(() => import("@/pages/Cart"));
const PreviousArtWork = lazy(() => import("@/pages/PreviousArtWork"));
const ProductColor = lazy(() => import("@/pages/ProductColor"));
const SelectAnOption = lazy(() => import("@/pages/SelectAnOption"));
const Upload = lazy(() => import("@/pages/Upload"));

const App = () => {
  const dispatch = useAppDispatch();

  const params = new URLSearchParams(window.location.search);
  const paramsId = params.get("id");
  const paramsCustomer = params.get("customer");
  const storeId = useAppSelector((state: RootState) => state.info.id);
  const storeCustomer = useAppSelector(
    (state: RootState) => state.info.customer,
  );
  const id = paramsId || storeId;
  const customer = paramsCustomer || storeCustomer;

  const dashboardData = useAppSelector(
    (state: RootState) => state.dashboard.data,
  );
  const { zoom } = useAppSelector((state: RootState) => state.designer);

  useEffect(() => {
    const updateZoom = (change: number) => {
      const newZoom = +zoom + change;
      if (newZoom >= 1.0 && newZoom <= 1.5) {
        dispatch(setZoom(newZoom));
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        updateZoom(e.deltaY > 0 ? -0.1 : 0.1);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey) {
        switch (e.key) {
          case "+":
            e.preventDefault();
            updateZoom(0.1);
            break;
          case "-":
            e.preventDefault();
            updateZoom(-0.1);
            break;
          case "0":
            dispatch(setZoom(1));
            break;
          default:
            break;
        }
      }
    };

    const preventPinchZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("touchmove", preventPinchZoom, {
      passive: false,
    });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("touchmove", preventPinchZoom);
    };
  }, [dispatch, zoom]);

  // const router = createBrowserRouter(
  //   [
  //     {
  //       path: "/",
  //       element: <RootLayout />,
  //       children: [
  //         {
  //           path: "",
  //           element: <DesignLayout />,
  //           children: [
  //             { path: "upload", element: <Upload /> },
  //             { path: "edit-image", element: <EditImage /> },
  //             { path: "add-text", element: <AddText /> },
  //             { path: "edit-text", element: <EditText /> },
  //             { path: "font-preview", element: <FontPreview /> },
  //             { path: "font-color", element: <FontColor /> },
  //             { path: "text-size", element: <TextSize /> },
  //             { path: "text-outline", element: <TextOutline /> },
  //             { path: "text-alignment", element: <TextAlignment /> },
  //             { path: "previous-art-work", element: <PreviousArtWork /> },
  //             { path: "product-color", element: <ProductColor /> },
  //             { path: "select-an-option", element: <SelectAnOption /> },
  //             { path: "add-notes", element: <AddNotes /> },
  //             { path: "", element: <Navigate to="product-color" replace /> },
  //           ],
  //         },
  //         { path: "approve-mockup", element: <ApproveMockup /> },
  //         { path: "cart", element: <Cart /> },
  //       ],
  //     },
  //   ],
   
  // );


  useEffect(() => {
    const fetchDashboard = async () => {
      if (!id && !customer) {
        window.location.href = "https://imprintmaker.com";
      }
      if (dashboardData) return;

      if (id) {
        dispatch(setId(id));
      }
      if (customer) {
        dispatch(setCustomer(customer));
      }

      if (id && customer) {
        const formData = new FormData();
        formData.append("product_id", id);
        formData.append("shop", "imprintmaker.myshopify.com");
        formData.append("customer", customer);

        dispatch(setTotal(4));
        dispatch(incrementCompleted());
        await dispatch(fetchDashboardData(formData));
      }
    };

    fetchDashboard();
  }, [dispatch, id, customer, dashboardData]);

  return (
    <Suspense fallback={<Loader />}>
      {/* <RouterProvider router={router} /> */}
      <BrowserRouter basename="/apps/product">
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route path="" element={<DesignLayout />}>
          <Route path="upload" element={<Upload />} />
          <Route path="edit-image" element={<EditImage />} />
          <Route path="add-text" element={<AddText />} />
          <Route path="edit-text" element={<EditText />} />
          <Route path="font-preview" element={<FontPreview />} />
          <Route path="font-color" element={<FontColor />} />
          <Route path="text-size" element={<TextSize />} />
          <Route path="text-outline" element={<TextOutline />} />
          <Route path="text-alignment" element={<TextAlignment />} />
          <Route path="previous-art-work" element={<PreviousArtWork />} />
          <Route path="product-color" element={<ProductColor />} />
          <Route path="select-an-option" element={<SelectAnOption />} />
          <Route path="add-notes" element={<AddNotes />} />
          <Route path="" element={<Navigate to="product-color" replace />} />
        </Route>
        <Route path="approve-mockup" element={<ApproveMockup />} />
        <Route path="cart" element={<Cart />} />
      </Route>
    </Routes>
  </BrowserRouter>
    </Suspense>
  );
};

export default App;